/**
 * DETAILS TAB — "this till reports a different model" (sunmi-card-setup.md §5).
 *
 * Any register, but it renders NOTHING unless the server says the till's
 * reported model disagrees with its catalog row (`mismatch: true`) and both
 * are known. Then it says so and offers the catalog model(s) that claim the
 * report (`suggestedModels`, the build-matching one first).
 */
import React, { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { api } from '../../../services/apiService';
import { toastService } from '../../../services/toastService';
import { RubyGemMark } from '../../RubyGemMark';
import { type DeviceModelReport, reportedHardware } from './useReportedModel';

/** Pure — exported for tests. True only for a real, known disagreement. */
export function isModelMismatch(r: DeviceModelReport | null | undefined): boolean {
  return !!r && r.mismatch === true && !!r.model && !!reportedHardware(r);
}

interface Props {
  tenantId: string;
  terminalId: string;
  report: DeviceModelReport | null | undefined;
  onSwitched?: (modelId: string) => void;
}

export const ModelMismatchBanner: React.FC<Props> = ({ tenantId, terminalId, report, onSwitched }) => {
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  if (!isModelMismatch(report)) return null;
  const r = report!;
  const said = reportedHardware(r);
  const brand = (r as any).reported?.hardwareBrand as string | undefined;
  const saidFull = brand && !said!.toLowerCase().startsWith(brand.toLowerCase()) ? `${brand} ${said}` : said;
  const suggestions = (r.suggestedModels || []).filter((m) => m.modelId !== r.model!.modelId);

  const use = async (modelId: string, name: string) => {
    setBusy(modelId); setErr(null);
    try {
      await api.posNmi.setTerminalDeviceModel(tenantId, terminalId, modelId);
      toastService.success(`Set up as ${name}.`);
      onSwitched?.(modelId);
    } catch (e: any) {
      const code = e?.code || e?.error;
      setErr(code === 'CARD_SETUP_PRESENT'
        ? 'This till has a card setup that model cannot carry — clear it on the Terminal (EMV/SoftPOS) tab first.'
        : e?.serverMessage || e?.message || 'The model was not changed.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div role="status" data-testid="model-mismatch" className="mb-4 flex flex-wrap items-center gap-3 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3">
      <AlertTriangle size={18} className="shrink-0 text-amber-600" />
      <p className="min-w-0 flex-1 text-sm text-stone-800">
        This till reports <b>{saidFull}</b>, but it&rsquo;s set up as <b>{r.model!.modelName}</b>.
        {!suggestions.length && <span className="text-stone-600"> No catalog model claims “{said}” yet — add it to a model’s hardware ids.</span>}
      </p>
      {suggestions.map((m) => (
        <button key={m.modelId} type="button" disabled={!!busy} onClick={() => use(m.modelId, m.modelName)}
          className="inline-flex min-h-[44px] items-center gap-2 rounded-lg bg-black px-4 text-sm font-semibold text-white hover:bg-stone-800 disabled:opacity-40">
          {busy === m.modelId && <RubyGemMark size={15} drawing color="currentColor" />}
          Use {m.modelName}
        </button>
      ))}
      {err && <p className="w-full text-xs text-ruby-deep">{err}</p>}
    </div>
  );
};

export default ModelMismatchBanner;
